// Original investing-method lessons; figures describe hypothetical scenarios only.
export const methodLessons = [
  {
    id: 'dca-mechanics', moduleId: 'methods',
    title: '每次投入一樣多，到底買到多少？', subtitle: '定期定額的單位數與平均成本', minutes: 7, tag: '投入機制',
    objectives: ['逐筆把投入金額換算成買到的單位數', '用總成本除以總單位數，避免直接平均價格'],
    paragraphs: [
      '定期定額是按規則，在固定間隔投入固定金額。價格較低時，同樣的錢能買到較多單位；價格較高時，買到較少。它讓投入行為有規律，但不會自動挑出好資產，也不保證最後獲利。',
      '先逐期計算「實際用於買入的金額 ÷ 成交價格」，再加總單位數。平均每單位成本是總買入成本除以總單位數，不是把每期價格直接相加平均，因為每個價格買到的數量不同。本課忽略費用；實際紀錄還要處理手續費、零股或小數單位規則，以及未投入的剩餘現金。',
      '平均成本比某些買入價格低，只說明持倉的取得成本。若期末價格更低，仍會虧損；價格一路上漲時，後續買入也會逐步墊高平均成本。把投入紀律、標的風險與期末成果分開檢查，才不會把「持續買」誤認為保證回本。',
    ],
    example: {
      title: '假設：連續三期各投入 2,400 元',
      steps: ['三期成交價分別為 24、16、30 元，忽略費用，買到 100、150、80 單位。', '總投入 7,200 元、總單位數 330，平均成本 = 7,200 ÷ 330，約 21.82 元；直接平均三個價格得到的 23.33 元不是持倉平均成本。', '若期末價格為 18 元，持倉價值 330 × 18 = 5,940 元，比總投入少 1,260 元；相對總投入為 −17.5%，但這不是考慮投入日期的年化報酬。'],
      conclusion: '定額投入改變了各價格買到的數量，沒有消除資產繼續下跌的可能。',
    },
    pitfalls: ['直接平均每次成交價，漏掉各次買到的數量不同。', '把買到更多單位當成已經賺錢，沒有重算當下市值。'],
    glossary: [{ term: '單位數', definition: '實際持有的基金單位或股票股數，與投入金額不是同一件事。' }, { term: '平均取得成本', definition: '本課指總買入成本除以總持有單位數；實際稅務成本或券商顯示方式需另查規則。' }],
    takeaway: '先算每筆買到多少，再算平均成本，最後才看目前值多少。',
    exercise: '建立四欄紙上紀錄：日期、買入金額、成交價、單位數。用兩組上漲與下跌價格各試一次，假設沒有費用，核對平均成本與期末市值。',
    quiz: { question: '忽略費用，分別以每單位 10 元與 20 元各買 1,000 元，平均每單位成本約為多少？', options: ['15 元', '13.33 元', '20 元'], answer: 1, explanation: '分別買到 100 與 50 單位，總成本 2,000 ÷ 總單位數 150，約 13.33 元；15 元是兩個價格的簡單平均。' },
    sources: [
      { label: '市場先生｜定期定額的主題閱讀', url: 'https://rich01.com/dollar-cost-averaging/', kind: 'reading' },
      { label: 'SEC｜定期定額的定義與買入數量', url: 'https://www.investor.gov/introduction-investing/investing-basics/glossary/dollar-cost-averaging', kind: 'official' },
    ],
  },
  {
    id: 'lump-sum-timing', moduleId: 'methods',
    title: '分批之前，先問錢何時已經到位', subtitle: '一次投入、現金等待與薪資投入', minutes: 7, tag: '釐清條件',
    objectives: ['分辨已有整筆資金與未來才收到收入的兩種情境', '把等待投入的現金也納入公平比較'],
    paragraphs: [
      '「一次投入或分批投入」要先確認起點。如果整筆現金今天已經可用，分批代表主動讓一部分暫時留在現金中；若每月薪水才陸續收到，則沒有同樣的整筆資金能在第一天投入。不能把尚未到帳的薪資假裝已經存在，拿來比較兩種方法。',
      '已有整筆錢時，一次投入讓全部金額立即承受投資的漲跌；分批則延後一部分市場曝險，也可能錯過上漲。公平比較應使用相同起始財富、相同終點，並把尚未投入的現金及其實際收益一起算。現金收益不一定為零，本課只是明確設定為零方便看懂。',
      '分批的結果受價格路徑、等待長度、費用與現金收益影響。歷史研究描述過去樣本，不會決定下一次哪個方法獲利較多。先把資金用途與可承擔風險釐清，再記錄投入規則，避免因短期漲跌不斷更改比較起點。',
    ],
    example: {
      title: '假設：6,000 元一開始就已備妥',
      steps: ['忽略交易費用，等待現金收益為零。方案甲在價格 20 元時全部投入，買 300 單位；方案乙先投入 3,000 元，剩下 3,000 元於下一個時點投入。', '若第二次價格 25 元，乙共買 150 + 120 = 270 單位；共同終點價格 30 元時，甲為 9,000 元，乙為 8,100 元。', '改成第二次價格 15 元、共同終點價格 12 元，甲為 300 × 12 = 3,600 元；乙共 150 + 200 = 350 單位，價值 4,200 元。兩者都低於原始 6,000 元。'],
      conclusion: '兩條假設路徑各有不同結果。若第二筆 3,000 元是後來才領到的薪資，就不符合第一天已有 6,000 元的比較條件。',
    },
    pitfalls: ['只比較已投入部位的報酬率，漏掉仍在等待的現金。', '因某一段回測勝出，就認定分批或一次投入永遠比較好。'],
    glossary: [{ term: '市場曝險', definition: '資金已經承受相關投資價格變動的程度。' }, { term: '現金等待成本', definition: '延後投入可能錯過的市場成果；市場也可能下跌，因此等待不一定造成損失。' }],
    takeaway: '先對齊資金到位日期與總財富，再比較投入方法。',
    exercise: '把未來三個月的可用投資資金分成「今天已到帳」與「未來預計收入」；不要填商品名稱，只列日期與假設，再確認你真正比較的是哪種情境。',
    quiz: { question: '每月領到薪水後才有新資金可投入，和把今天已有的一筆錢分批投入，有何關鍵差別？', options: ['資金可用日期不同，不能假設薪資全在第一天就能投資', '只要最後總投入相同，就能忽略到帳日期', '領薪後投入一定能買到更低價格'], answer: 0, explanation: '分批運用已有現金是一項曝險時點選擇；未來薪資尚不可用，兩者起點不同。兩種情境都沒有買價或報酬保證。' },
    sources: [
      { label: '市場先生｜單筆與定額的可用資金差異', url: 'https://rich01.com/how-to-choose-between-regular-fixed-amount-and-single-investment/', kind: 'reading' },
      { label: 'Vanguard｜整筆現金的一次投入與分批投入', url: 'https://investor.vanguard.com/investor-resources-education/online-trading/dollar-cost-averaging-vs-lump-sum', kind: 'official' },
    ],
  },
  {
    id: 'time-weighted-return', moduleId: 'methods',
    title: '帳戶增加很多，是賺的還是自己存的？', subtitle: '用 TWR 分離外部現金流', minutes: 8, tag: '衡量績效',
    objectives: ['在外部投入或提領前後切分報酬區間', '連乘分段報酬，區分累積成果與金額損益'],
    paragraphs: [
      '時間加權報酬（TWR）要回答的是：排除外部資金進出帶來的規模變化，這段投資本身表現如何？它不是把每次報酬做算術平均，也不是直接拿期末餘額除以總投入。投入資金可能讓帳戶變大，但這部分不是投資獲利。',
      '本課採精確分段的簡化方式：每遇外部現金流，就取得發生前的帳戶價值，結束上一段；加入或扣除現金流後的價值，作為下一段起點。沒有其他外部現金流的每段報酬 = 段末價值 ÷ 段初價值 − 1，再把各段（1 + 報酬）相乘，最後減 1。',
      '「外部」取決於你畫出的帳戶範圍。帳戶內賣股票變現金、股息留在同一帳戶，通常仍是內部變動，不能當成外部存款扣掉。報酬也要先統一費用與幣別口徑。分段連乘得到該整段期間的累積 TWR，只有再考慮期間長度後，才能談年化。',
    ],
    example: {
      title: '假設：一年的中途加碼，和獲利分開記',
      steps: ['年初帳戶 10,000 元，上半年結束、加碼前為 12,000 元，第一段報酬為 20%。', '隨即外部投入 12,000 元，第二段起點為 24,000 元；年底剩 21,600 元，第二段報酬為 −10%。期間沒有其他外部現金流。', '全年 TWR = 1.2 × 0.9 − 1 = 8%；但總投入 22,000 元，最後 21,600 元，實際金額損益為 −400 元。'],
      conclusion: 'TWR 為正和個人金額損益為負可以同時成立，因為較大的資金剛好參與了下跌的那一段。',
    },
    pitfalls: ['把外部新存入的錢計成投資報酬，或把帳戶內買賣當成外部流入流出。', '把兩段 +20%、−10% 直接相加成 +10%，忽略連乘後是 +8%。'],
    glossary: [{ term: '外部現金流', definition: '穿越所衡量投資帳戶邊界的投入或提領；範圍須先定義。' }, { term: '幾何連結', definition: '將各段成長因子（1 + 報酬率）相乘，以得到整段期間的累積成果。' }],
    takeaway: '每次外部現金流都先切段，再連乘報酬，存進去的錢才不會被算成賺到的錢。',
    exercise: '畫一條時間線，標出期初、每次外部投入或提領前後，以及期末帳戶價值。分別算每段報酬與累積 TWR，最後另列金額損益。',
    quiz: { question: '帳戶由 1,000 增為 1,100 元，隨即外部投入 900 元，再由 2,000 增為 2,200 元；無其他現金流，兩段累積 TWR 是多少？', options: ['20%', '120%', '21%'], answer: 2, explanation: '兩段各為 10%，所以 1.1 × 1.1 − 1 = 21%。900 元是外部投入，不是第一段的獲利。此題沒有指定期間長度，因此不是年化數字。' },
    sources: [
      { label: '市場先生｜時間加權與資金加權報酬', url: 'https://rich01.com/what-is-twrr-vs-mwrr/', kind: 'reading' },
      { label: 'CFA Institute｜GIPS 的 TWR 與外部現金流說明', url: 'https://rpc.cfainstitute.org/sites/default/files/docs/codes-and-standards/introduction-to-the-gips-standards-for-asset-owners_requirements_online.pdf', kind: 'official' },
      { label: 'CFA Institute｜分段報酬與幾何連結方法', url: 'https://www.cfainstitute.org/-/media/documents/code/gips/2020-gips-standards-asset-owners.pdf', kind: 'official' },
    ],
  },
  {
    id: 'money-weighted-return', moduleId: 'methods',
    title: '同樣投入總額，早一年與晚一年不同', subtitle: '資金加權報酬、IRR 與現金流日期', minutes: 8, tag: '個人成果',
    objectives: ['依投資人角度記錄投入、提領及期末價值', '理解 IRR 會受到金額、時點與解的唯一性影響'],
    paragraphs: [
      '資金加權報酬（MWR）反映你的資金在什麼時候、以多少金額參與投資，常用內部報酬率 IRR 表達。它尋找一個折現率，使所有現金流的淨現值等於零。從投資人角度，投入記負數，取回記正數；評估日尚未賣出的帳戶價值，可當作當天可取回的正數，但要用一致估值且避免重複計算。',
      'IRR 處理等間隔現金流，算出的是每個間隔的比率；只有一年一期時才直接是年率。日期不規則時，可用 XIRR 依實際日期計算年化結果。先核對時間線和正負號，再看數字；一個很高的短期年化數字，也不表示未來能持續以相同速度獲利。',
      'MWR 與 TWR 回答的問題不同，不能只挑比較漂亮的一個。現金流多次改變正負號時，IRR 可能出現多個解，也可能找不到合適解；試算表回傳一個結果不代表它唯一。這時應保留完整現金流、金額損益及期末估值，並檢查不同折現率下的淨現值，而非用單一比率下結論。',
    ],
    example: {
      title: '假設：第二筆錢只投資了一年',
      steps: ['今天投入 20,000 元，一年後再投入 10,000 元，兩年後全部取回 35,200 元；忽略費用，現金流依序為 −20,000、−10,000、+35,200。', '代入年率 10%：20,000 × 1.1² + 10,000 × 1.1 = 35,200，所以這組現金流的年 IRR 為 10%。', '若誤把 30,000 元全當成第一天投入，再算（35,200 ÷ 30,000）的平方根減 1，只得到約 8.32%；它算的是另一條不存在的資金時間線。'],
      conclusion: '年化之前先還原日期；總投入一樣，不代表每一元都在市場待了一樣久。',
    },
    pitfalls: ['直接把金額獲利除以總投入，再除以年數，當成有多次現金流的 IRR。', '漏記期末尚未賣出的資產，或把同筆配息與帳戶價值重複列入取回金額。'],
    glossary: [{ term: '淨現值（NPV）', definition: '將各時點現金流按指定折現率換算到同一時點後相加的金額。' }, { term: 'XIRR', definition: '依每筆實際日期計算年化內部報酬率的試算表函數，仍須檢查輸入與解的合理性。' }],
    takeaway: 'MWR 衡量你的資金經驗，日期和金額都不能省略。',
    exercise: '進階紙上練習：三個相隔一年的現金流為 −1,000、+2,500、−1,560。分別將 20% 與 30% 代入「−1,000 + 2,500 ÷（1+r）− 1,560 ÷（1+r）²」，確認兩者都得到零，觀察為何不能任選一個當唯一答案。',
    quiz: { question: '要比較自己多次投入、不同日期提領的資金加權成果，最需要哪項資料？', options: ['只要知道總投入與總取回即可', '每筆現金流金額、日期，以及評估日的剩餘價值', '只要找到期間最高股價'], answer: 1, explanation: 'MWR 會受到每筆錢參與時間與金額影響；缺少日期或剩餘資產價值，無法完整還原個人的現金流成果。' },
    sources: [
      { label: '市場先生｜IRR 內部報酬率', url: 'https://rich01.com/irr/', kind: 'reading' },
      { label: 'Microsoft｜IRR、NPV 與多個解的限制', url: 'https://support.microsoft.com/en-us/excel/go-with-the-cash-flow-calculate-npv-and-irr-in-excel', kind: 'official' },
      { label: 'Microsoft｜XIRR 日期與年化計算規則', url: 'https://support.microsoft.com/en-us/excel/functions/xirr-function', kind: 'official' },
    ],
  },
  {
    id: 'fee-compounding', moduleId: 'methods',
    title: '每年少一點，二十年後差在哪裡？', subtitle: '持續費用與少掉的複利機會', minutes: 8, tag: '長期成本',
    objectives: ['用明確扣費時點計算多年資產路徑', '區分已付費用總額與期末財富差距'],
    paragraphs: [
      '持續費用不只減少當期資產，也改變之後參與漲跌的金額。在正成長的假設中，已付出去的費用無法留在帳戶繼續複利，因此長期財富差距可能大於逐年費用相加。這是機會成本，不是業者另外收了同樣金額的第二張帳單。',
      '試算必須說清楚扣費時點。本課刻意採「每年先取得 6% 費前報酬，再按當時資產扣 1% 費用」的簡化模型，每年留下的成長因子為 1.06 × 0.99 = 1.0494，而不是精確的 1.05。真實基金常按日計提費用，不能把本例視為任何商品的收費公式。',
      '多年比較要使用相同費前投資路徑，才能把差異歸因於費用。未來報酬並不固定；費用較低也不會保證資產上漲。若資料已是扣費後的淨值報酬，就不能再扣同項費用。費用率、交易成本與投資範圍仍要分別查清楚，避免拿完全不同策略只比價格。',
    ],
    example: {
      title: '假設：10 萬元、20 年、不追加也不提領',
      steps: ['每年費前報酬固定 6%，年末按費前期末資產收 1%；第一年先到 106,000 元，扣 1,060 元後剩 104,940 元。此報酬與扣費方式只供計算。', '完全不收費的模型終值 = 100,000 × 1.06²⁰，約 320,714 元；收費模型 = 100,000 ×（1.06 × 0.99）²⁰，約 262,314 元，差約 58,400 元。', '沿著收費模型逐年加總實付費用，約為 34,828 元；其餘約 23,571 元是那些費用若留下、依同一假設繼續成長的機會。各數字分別四捨五入會有 1 元差異。'],
      conclusion: '不能用第一年費用乘 20 代替多年試算，也不能把 58,400 元全說成直接付給業者的費用。',
    },
    pitfalls: ['只用起始本金乘年費率再乘年數，假設資產額永遠不變。', '把費前報酬減年費率的近似值當成任何扣費方式都精確成立。'],
    glossary: [{ term: '費前與費後報酬', definition: '是否已反映指定費用的報酬口徑；比較或試算前要知道扣了哪些項目。' }, { term: '機會成本', definition: '選擇一條路徑後放棄的另一種可能成果；本課用同一假設下未能留存複利的金額說明。' }],
    takeaway: '長期費用比較要看整條資產路徑，不能只把單年帳單加總。',
    exercise: '用同一個年末扣費模型，各試 5 年與 20 年，再把費前報酬改成 0%。分別列出終值、實付費用與無費用模型的差距，觀察哪部分來自成長假設。',
    quiz: { question: '依本課扣費時點，先取得 5% 費前報酬，再按當時資產收 1%，一年成長因子是多少？', options: ['1.0395', '1.04（在此模型中精確成立）', '1.05'], answer: 0, explanation: '1.05 × 0.99 = 1.0395，也就是費後 3.95%。直接用 5% − 1% = 4% 是另一種近似，和題目的扣費時點不完全相同。' },
    sources: [
      { label: '市場先生｜ETF 持續費用的主題閱讀', url: 'https://rich01.com/etf-all-expense/', kind: 'reading' },
      { label: 'SEC｜費用如何改變長期投資組合', url: 'https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins/updated', kind: 'official' },
    ],
  },
  {
    id: 'rebalancing-contributions', moduleId: 'methods',
    title: '把新錢放哪裡，比例才能回來？', subtitle: '以投入資金再平衡與金額限制', minutes: 7, tag: '維護配置',
    objectives: ['以投入後的新總值計算每類資產的目標金額', '辨認新資金不足時無法只買不賣的情況'],
    paragraphs: [
      '新增資金可以用來補足比重偏低的資產，讓組合靠近原先設定的配置，並減少賣出需求。它依然是管理配置的方法，不保證提高報酬；本課比例只是算術示範，不是適合每個人的投資比例。',
      '先把目前各類市值加總，再加上本次確實可投入的新資金。以「投入後總值 × 目標權重」計算每類目標金額，減去目前市值，就是需要增加的金額。全部差額都非負，而且合計等於新資金時，在忽略交易限制與費用的模型中，才可能只靠買入精確回到目標。',
      '若其中一類差額為負，表示新總值下它仍超標，單靠本次新增金額無法完全調整。可以量化投入後還偏離多少，再依事先規則評估保留偏離、等待後續現金流或另作調整；不能只把負數刪掉，卻宣稱已達目標。實際還要考慮費用、最小交易單位與期間價格變動。',
    ],
    example: {
      title: '假設：股票 72,000 元、債券 48,000 元',
      steps: ['練習目標各占 50%。若新增 30,000 元，總值變 150,000 元，目標各為 75,000 元，因此股票買 3,000 元、債券買 27,000 元即可在模型中達標。', '若本次只有 10,000 元，總值 130,000 元，目標各為 65,000 元；股票已超出 7,000 元，所以不能只靠這 10,000 元買入就精確回到 50／50。', '把這 10,000 元全放債券後，股票仍占 72,000 ÷ 130,000，約 55.38%。若價格不變且堅持不賣，至少需新增 24,000 元至債券，才會得到 72,000 對 72,000 元。'],
      conclusion: '新錢有助縮小偏離，但夠不夠必須計算；補進去之後的分母也會改變。',
    },
    pitfalls: ['沿用投入前總值計算目標，忽略新資金讓分母變大。', '把所有新增資金放進比重偏低的一類，就以為每次都能精確達標。'],
    glossary: [{ term: '目標金額', definition: '以本次投入後的總值乘事先設定的目標權重，得到每類資產應有的金額。' }, { term: '權重偏離', definition: '實際比例與目標比例的差，通常用百分點表示；偏離不等於已實現虧損。' }],
    takeaway: '先重算投入後分母，再檢查每個差額是否都能用買入完成。',
    exercise: '延續例題，把新增資金改成 5,000、24,000、40,000 元，各算目標金額與買入差額；圈出哪組無法只買不賣達標，並算出全補債券後的股票比例。',
    quiz: { question: '股票 45,000 元、債券 55,000 元，新增 20,000 元，練習目標各 50%；忽略費用與價格變動，應各買多少？', options: ['股票 10,000、債券 10,000 元', '股票 20,000、債券 0 元', '股票 15,000、債券 5,000 元'], answer: 2, explanation: '投入後總值 120,000 元，目標各 60,000 元；股票差 15,000 元、債券差 5,000 元，合計正好是新資金 20,000 元。' },
    sources: [
      { label: '市場先生｜資產配置再平衡', url: 'https://rich01.com/what-asset-rebalancing/', kind: 'reading' },
      { label: 'SEC｜新增投入與再平衡的不同方式', url: 'https://www.investor.gov/additional-resources/general-resources/publications-research/info-sheets/beginners-guide-asset', kind: 'official' },
    ],
  },
];
